import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useState, useMemo } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useSmartCardContext } from '@atlaskit/link-provider';
import { Card, getObjectAri, getObjectIconUrl, getObjectName } from '@atlaskit/smart-card';
import { useSmartLinkActions } from '@atlaskit/smart-card/hooks';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { HoverLinkOverlay, UnsupportedInline } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { CardErrorBoundary } from './fallback';
import { withSmartCardStorage } from '../../ui/SmartCardStorage';
import { getCardClickHandler } from '../utils/getCardClickHandler';
import { useInlineAnnotationProps } from '../../ui/annotations/element/useInlineAnnotationProps';
import { usePortal } from '../../ui/Renderer/PortalContext';
var HoverLinkOverlayNoop = function HoverLinkOverlayNoop(props) {
  return jsx(Fragment, null, props.children);
};
var HoverLinkOverlayWithCondition = componentWithCondition(function () {
  return editorExperiment('platform_editor_preview_panel_linking_exp', true, {
    exposure: true
  });
}, HoverLinkOverlay, HoverLinkOverlayNoop);
var OverlayWithCardContext = function OverlayWithCardContext(_ref) {
  var _cardContext$value2, _cardContext$value3, _cardContext$value3$i, _cardContext$value4;
  var rendererAppearance = _ref.rendererAppearance,
    isResolvedViewRendered = _ref.isResolvedViewRendered,
    url = _ref.url,
    fireAnalyticsEvent = _ref.fireAnalyticsEvent,
    children = _ref.children;
  var cardContext = useSmartCardContext();
  // Note: useSmartLinkActions throws without smart card context. Using it here is safe
  // because we checked cardContext availability in the parent component
  var actions = useSmartLinkActions({
    url: url,
    appearance: 'inline'
  });
  var preview = useMemo(function () {
    return actions.find(function (action) {
      return action.id === 'preview-content';
    });
  }, [actions]);
  var fireHoverLabelAEP = function fireHoverLabelAEP(previewType) {
    if (fireAnalyticsEvent) {
      var _cardContext$value, _urlState$details$met, _urlState$details, _urlState$details$met2, _urlState$details2;
      var store = cardContext === null || cardContext === void 0 || (_cardContext$value = cardContext.value) === null || _cardContext$value === void 0 ? void 0 : _cardContext$value.store;
      var urlState = store === null || store === void 0 ? void 0 : store.getState()[url || ''];
      fireAnalyticsEvent({
        action: ACTION.CLICKED,
        actionSubject: ACTION_SUBJECT.SMART_LINK,
        actionSubjectId: ACTION_SUBJECT_ID.HOVER_LABEL,
        eventType: EVENT_TYPE.UI,
        attributes: {
          previewType: previewType,
          destinationProduct: (_urlState$details$met = urlState === null || urlState === void 0 || (_urlState$details = urlState.details) === null || _urlState$details === void 0 || (_urlState$details = _urlState$details.meta) === null || _urlState$details === void 0 ? void 0 : _urlState$details.product) !== null && _urlState$details$met !== void 0 ? _urlState$details$met : null,
          destinationSubproduct: (_urlState$details$met2 = urlState === null || urlState === void 0 || (_urlState$details2 = urlState.details) === null || _urlState$details2 === void 0 || (_urlState$details2 = _urlState$details2.meta) === null || _urlState$details2 === void 0 ? void 0 : _urlState$details2.subproduct) !== null && _urlState$details$met2 !== void 0 ? _urlState$details$met2 : null
        }
      });
    }
  };
  var cardState = cardContext === null || cardContext === void 0 || (_cardContext$value2 = cardContext.value) === null || _cardContext$value2 === void 0 || (_cardContext$value2 = _cardContext$value2.store) === null || _cardContext$value2 === void 0 ? void 0 : _cardContext$value2.getState()[url || ''];
  var ari = getObjectAri(cardState === null || cardState === void 0 ? void 0 : cardState.details);
  var name = getObjectName(cardState === null || cardState === void 0 ? void 0 : cardState.details);
  var iconUrl = getObjectIconUrl(cardState === null || cardState === void 0 ? void 0 : cardState.details);
  // Get resolved URL from card state, fallback to original URL if not available
  var resolvedUrl = url;
  if (expValEquals('platform_hover_card_preview_panel', 'cohort', 'test')) {
    var _cardState$details;
    var cardStateUrl = cardState !== null && cardState !== void 0 && (_cardState$details = cardState.details) !== null && _cardState$details !== void 0 && _cardState$details.data && 'url' in cardState.details.data ? cardState.details.data.url : undefined;
    resolvedUrl = cardStateUrl || url;
  }
  var isPanelAvailable = ari && (cardContext === null || cardContext === void 0 || (_cardContext$value3 = cardContext.value) === null || _cardContext$value3 === void 0 || (_cardContext$value3$i = _cardContext$value3.isPreviewPanelAvailable) === null || _cardContext$value3$i === void 0 ? void 0 : _cardContext$value3$i.call(_cardContext$value3, {
    ari: ari
  }));
  var openPreviewPanel = cardContext === null || cardContext === void 0 || (_cardContext$value4 = cardContext.value) === null || _cardContext$value4 === void 0 ? void 0 : _cardContext$value4.openPreviewPanel;
  var isPreviewPanelAvailable = Boolean(openPreviewPanel && isPanelAvailable);
  var isPreviewModalAvailable = Boolean(preview);
  var isPreviewAvailable = isPreviewModalAvailable || isPreviewPanelAvailable;
  var showPanelButtonIcon = isPreviewPanelAvailable ? 'panel' : isPreviewModalAvailable ? 'modal' : undefined;
  var Overlay = isPreviewAvailable ? HoverLinkOverlayWithCondition : HoverLinkOverlayNoop;
  return jsx(Overlay, {
    isVisible: isResolvedViewRendered,
    url: url,
    compactPadding: rendererAppearance === 'comment',
    showPanelButton: isPreviewAvailable,
    showPanelButtonIcon: showPanelButtonIcon,
    onClick: function onClick(event) {
      if (isPreviewPanelAvailable) {
        // Prevent anchor default behaviour(click to open the anchor link)
        // When glance panel is available, let openPreviewPanel handle it
        event.preventDefault();
        openPreviewPanel === null || openPreviewPanel === void 0 || openPreviewPanel({
          url: resolvedUrl || '',
          ari: ari || '',
          name: name || '',
          iconUrl: iconUrl
        });
        editorExperiment('platform_editor_preview_panel_linking_exp', true, {
          exposure: true
        }) && fireHoverLabelAEP('panel');
      } else if (isPreviewModalAvailable) {
        event.preventDefault();
        if (preview) {
          preview.invoke();
        }
        editorExperiment('platform_editor_preview_panel_linking_exp', true, {
          exposure: true
        }) && fireHoverLabelAEP('modal');
      }
    }
  }, children);
};
var InlineCard = function InlineCard(props) {
  var url = props.url,
    data = props.data,
    eventHandlers = props.eventHandlers,
    fireAnalyticsEvent = props.fireAnalyticsEvent,
    smartLinks = props.smartLinks,
    rendererAppearance = props.rendererAppearance;
  var portal = usePortal(props);
  var cardContext = useSmartCardContext();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isResolvedViewRendered = _useState2[0],
    setIsResolvedViewRendered = _useState2[1];
  var onClick = getCardClickHandler(eventHandlers, url);
  var cardProps = {
    url: url,
    data: data,
    onClick: onClick,
    container: portal
  };
  var _ref2 = smartLinks || {},
    hideHoverPreview = _ref2.hideHoverPreview,
    actionOptions = _ref2.actionOptions,
    ssr = _ref2.ssr;
  var analyticsData = {
    attributes: {
      location: 'renderer'
    },
    // Below is added for the future implementation of Linking Platform namespaced analytic context
    location: 'renderer'
  };
  var inlineAnnotationProps = useInlineAnnotationProps(props);
  var CompetitorPrompt = smartLinks === null || smartLinks === void 0 ? void 0 : smartLinks.CompetitorPrompt;
  var CompetitorPromptComponent = CompetitorPrompt && url && fg('prompt_whiteboard_competitor_link_gate') ? jsx(CompetitorPrompt, {
    sourceUrl: url,
    linkType: "inline"
  }) : null;
  var onError = function onError(_ref3) {
    var err = _ref3.err;
    if (err) {
      throw err;
    }
  };
  var MaybeOverlay = cardContext !== null && cardContext !== void 0 && cardContext.value ? OverlayWithCardContext : HoverLinkOverlayNoop;
  if (ssr && url && !editorExperiment('platform_editor_preview_panel_linking_exp', true, {
    exposure: true
  })) {
    if (
    // eslint-disable-next-line @atlaskit/platform/no-invalid-feature-flag-usage
    fg('editor_inline_comments_on_inline_nodes')) {
      return jsx("span", _extends({
        "data-inline-card": true,
        "data-card-data": data ? JSON.stringify(data) : undefined,
        "data-card-url": url
        // Ignored via go/ees005
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, inlineAnnotationProps), jsx(AnalyticsContext, {
        data: analyticsData
      }, jsx(CardSSR, {
        appearance: "inline",
        url: url,
        showHoverPreview: !hideHoverPreview,
        actionOptions: actionOptions,
        onClick: onClick
      })));
    }
    return jsx(AnalyticsContext, {
      data: analyticsData
    }, jsx(CardSSR, {
      appearance: "inline",
      url: url,
      showHoverPreview: !hideHoverPreview,
      actionOptions: actionOptions,
      onClick: onClick
    }));
  } else if (ssr && url && editorExperiment('platform_editor_preview_panel_linking_exp', true, {
    exposure: true
  })) {
    if (
    // eslint-disable-next-line @atlaskit/platform/no-invalid-feature-flag-usage
    fg('editor_inline_comments_on_inline_nodes')) {
      return jsx("span", {
        "data-inline-card": true,
        "data-card-data": data ? JSON.stringify(data) : undefined,
        "data-card-url": url,
        "data-renderer-mark": inlineAnnotationProps['data-renderer-mark'],
        "data-annotation-draft-mark": inlineAnnotationProps['data-annotation-draft-mark'],
        "data-annotation-inline-node": inlineAnnotationProps['data-annotation-inline-node'],
        "data-renderer-start-pos": inlineAnnotationProps['data-renderer-start-pos'],
        "data-annotation-mark": inlineAnnotationProps['data-annotation-mark']
      }, jsx(AnalyticsContext, {
        data: analyticsData
      }, jsx(MaybeOverlay, {
        url: url || '',
        rendererAppearance: rendererAppearance,
        isResolvedViewRendered: isResolvedViewRendered,
        fireAnalyticsEvent: fireAnalyticsEvent
      }, jsx(CardSSR, {
        appearance: "inline",
        url: url,
        showHoverPreview: !hideHoverPreview,
        actionOptions: actionOptions,
        onClick: onClick,
        onResolve: function onResolve(data) {
          if (!data.url || !data.title) {
            return;
          }
          props.smartCardStorage.set(data.url, data.title);
          if (data.title) {
            setIsResolvedViewRendered(true);
          }
        },
        onError: onError,
        disablePreviewPanel: true
      }))));
    }
    return jsx(AnalyticsContext, {
      data: analyticsData
    }, jsx(MaybeOverlay, {
      url: url || '',
      rendererAppearance: rendererAppearance,
      isResolvedViewRendered: isResolvedViewRendered,
      fireAnalyticsEvent: fireAnalyticsEvent
    }, jsx(CardSSR, {
      appearance: "inline",
      url: url,
      showHoverPreview: !hideHoverPreview,
      actionOptions: actionOptions,
      onClick: onClick,
      onResolve: function onResolve(data) {
        if (!data.url || !data.title) {
          return;
        }
        props.smartCardStorage.set(data.url, data.title);
        if (data.title) {
          setIsResolvedViewRendered(true);
        }
      },
      onError: onError,
      disablePreviewPanel: true
    })));
  }
  return jsx(AnalyticsContext, {
    data: analyticsData
  }, jsx("span", _extends({
    "data-inline-card": true,
    "data-card-data": data ? JSON.stringify(data) : undefined,
    "data-card-url": url
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, inlineAnnotationProps), jsx(CardErrorBoundary, _extends({
    unsupportedComponent: UnsupportedInline
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, cardProps), jsx(MaybeOverlay, {
    url: url || '',
    rendererAppearance: rendererAppearance,
    isResolvedViewRendered: isResolvedViewRendered,
    fireAnalyticsEvent: fireAnalyticsEvent
  }, jsx(Card, _extends({
    appearance: "inline",
    showHoverPreview: !hideHoverPreview,
    actionOptions: actionOptions
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, cardProps, {
    onResolve: function onResolve(data) {
      if (!data.url || !data.title) {
        return;
      }
      props.smartCardStorage.set(data.url, data.title);
      if (data.title) {
        setIsResolvedViewRendered(true);
      }
    },
    onError: onError,
    disablePreviewPanel: editorExperiment('platform_editor_preview_panel_linking_exp', true, {
      exposure: true
    })
  }))), CompetitorPromptComponent)));
};
export default withSmartCardStorage(InlineCard);